import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { previousCloseService } from '../../services/previousCloseService';

// Async thunks for API calls
export const fetchPreviousCloseData = createAsyncThunk(
    'previousClose/fetchPreviousCloseData',
    async (symbol, { rejectWithValue }) => {
        try {
            const response = await previousCloseService.getPreviousCloseData(symbol);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { error: 'Failed to fetch previous close data' }
            );
        }
    }
);

export const fetchAllPreviousCloseData = createAsyncThunk(
    'previousClose/fetchAllPreviousCloseData',
    async (_, { rejectWithValue }) => {
        try {
            const response = await previousCloseService.getAllPreviousCloseData();
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { error: 'Failed to fetch all previous close data' }
            );
        }
    }
);

export const fetchAvailableSymbols = createAsyncThunk(
    'previousClose/fetchAvailableSymbols',
    async (_, { rejectWithValue }) => {
        try {
            const response = await previousCloseService.getAvailableSymbols();
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { error: 'Failed to fetch symbols' }
            );
        }
    }
);

export const fetchSymbolData = createAsyncThunk(
    'previousClose/fetchSymbolData',
    async (symbol, { rejectWithValue }) => {
        try {
            const response = await previousCloseService.fetchSymbolData(symbol);
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { error: 'Failed to fetch symbol data' }
            );
        }
    }
);

export const startDataFetching = createAsyncThunk(
    'previousClose/startDataFetching',
    async (_, { rejectWithValue }) => {
        try {
            const response = await previousCloseService.startDataFetching();
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { error: 'Failed to start data fetching' }
            );
        }
    }
);

export const stopDataFetching = createAsyncThunk(
    'previousClose/stopDataFetching',
    async (_, { rejectWithValue }) => {
        try {
            const response = await previousCloseService.stopDataFetching();
            return response;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || { error: 'Failed to stop data fetching' }
            );
        }
    }
);

const initialState = {
    // Single symbol data
    currentSymbolData: null,
    currentSymbol: 'USDINR',

    // All data (admin)
    allData: null,

    // Available symbols
    availableSymbols: [],

    // UI states
    loading: false,
    fetchingAll: false,
    symbolsLoading: false,

    // Error states
    error: null,

    // Metadata
    responseTime: null,
    dataAge: null,
    source: null,
    lastUpdate: null,

    // Admin states
    fetchingStatus: null, // 'started' | 'stopped' | null
};

const previousCloseSlice = createSlice({
    name: 'previousClose',
    initialState,
    reducers: {
        setCurrentSymbol: (state, action) => {
            state.currentSymbol = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetPreviousCloseState: (state) => {
            return { ...initialState, availableSymbols: state.availableSymbols };
        },
        clearCurrentData: (state) => {
            state.currentSymbolData = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch previous close data for specific symbol
            .addCase(fetchPreviousCloseData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPreviousCloseData.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSymbolData = action.payload.data;
                state.responseTime = action.payload.response_time_ms;
                state.dataAge = action.payload.data_age_seconds;
                state.source = action.payload.source;
                state.lastUpdate = action.payload.timestamp;
                state.error = null;
            })
            .addCase(fetchPreviousCloseData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to fetch previous close data';
            })

            // Fetch all previous close data (admin)
            .addCase(fetchAllPreviousCloseData.pending, (state) => {
                state.fetchingAll = true;
                state.error = null;
            })
            .addCase(fetchAllPreviousCloseData.fulfilled, (state, action) => {
                state.fetchingAll = false;
                state.allData = action.payload.data;
                state.lastUpdate = action.payload.timestamp;
                state.error = null;
            })
            .addCase(fetchAllPreviousCloseData.rejected, (state, action) => {
                state.fetchingAll = false;
                state.error = action.payload?.error || 'Failed to fetch all previous close data';
            })

            // Fetch available symbols
            .addCase(fetchAvailableSymbols.pending, (state) => {
                state.symbolsLoading = true;
                state.error = null;
            })
            .addCase(fetchAvailableSymbols.fulfilled, (state, action) => {
                state.symbolsLoading = false;
                state.availableSymbols = action.payload.symbols || [];
                state.error = null;
            })
            .addCase(fetchAvailableSymbols.rejected, (state, action) => {
                state.symbolsLoading = false;
                state.error = action.payload?.error || 'Failed to fetch symbols';
            })

            // Manual fetch symbol data (admin)
            .addCase(fetchSymbolData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSymbolData.fulfilled, (state, action) => {
                state.loading = false;
                state.currentSymbolData = action.payload.data;
                state.source = action.payload.source;
                state.error = null;
            })
            .addCase(fetchSymbolData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || 'Failed to fetch symbol data';
            })

            // Start data fetching (admin)
            .addCase(startDataFetching.fulfilled, (state, action) => {
                state.fetchingStatus = 'started';
                console.log('Previous close data fetching started:', action.payload.message);
            })

            // Stop data fetching (admin)
            .addCase(stopDataFetching.fulfilled, (state, action) => {
                state.fetchingStatus = 'stopped';
                console.log('Previous close data fetching stopped:', action.payload.message);
            });
    }
});

export const {
    setCurrentSymbol,
    clearError,
    resetPreviousCloseState,
    clearCurrentData
} = previousCloseSlice.actions;

// Selectors
export const selectCurrentSymbolData = (state) => state.previousClose.currentSymbolData;
export const selectCurrentSymbol = (state) => state.previousClose.currentSymbol;
export const selectAllPreviousCloseData = (state) => state.previousClose.allData;
export const selectAvailableSymbols = (state) => state.previousClose.availableSymbols;
export const selectPreviousCloseLoading = (state) => state.previousClose.loading;
export const selectPreviousCloseError = (state) => state.previousClose.error;
export const selectResponseTime = (state) => state.previousClose.responseTime;
export const selectDataAge = (state) => state.previousClose.dataAge;
export const selectDataSource = (state) => state.previousClose.source;

export default previousCloseSlice.reducer;